import { getPrimaryImage } from '../utils/getPrimaryImage'
import { htmlToPlainText } from './htmlToPlainText'

/**
 * Build the schema.org Product node body from CMS content.
 *
 * Pure function (no Nuxt auto-imports) so it can be unit-tested in isolation.
 * Wrap the return value with defineProduct() at the call site only when it is rated,
 * since the Product resolver would re-add the Product type.
 *
 * When content.product.trustpilotReviews is present (populated server-side by
 * /api/products/content from the Trustpilot Product Reviews API), maps it into
 * aggregateRating + review[] entries for Google Product rich results.
 */
export function buildProductSchema(content: any, name: string): any {
  // Prefer the manufacturer SKU (the identifier consumers search by and that
  // matches Trustpilot's product-reviews index) when the server-side resolver
  // has populated it. Falls back to product.skus (180 Medical's internal SKU)
  // when the manufacturer SKU isn't available.
  const sku = content?.product?.manufacturerSku || content?.product?.skus
  const tp = content?.product?.trustpilotReviews
  // P180PCM-558: pick by isPrimary, not array position — see getPrimaryImage.ts.
  const primaryImage = getPrimaryImage(content?.product?.images)
  const rawDescription = content?.product?.description?.fragments?.[0]?.internalFormat
  const brand = content?.product?.brand
  const gtin = content?.product?.gtin

  // Product.name should be the clean product display title (no "| 180 Medical"
  // SEO suffix). Falls back to the passed-in name (the resolved SEO title) for
  // safety when product.title is absent.
  const productName = htmlToPlainText(content?.product?.title) || name

  const product: any = {
    // Product only when rated: we never emit offers or review, and Google rejects a Product
    // snippet with none of offers/review/aggregateRating. MedicalDevice is always kept.
    '@type': tp?.aggregate ? ['Product', 'MedicalDevice'] : 'MedicalDevice',
    'name': productName,
    'description': htmlToPlainText(rawDescription?.replaceAll('\\n', ' ')),
    ...(sku ? { sku } : {}),
    ...(brand ? { brand: { '@type': 'Brand', 'name': brand } } : {}),
    ...(gtin ? { gtin } : {}),
  }

  // Emit image as a proper ImageObject (schema.org-spec compliant). The CMS
  // exposes product.images[] as `{ url, altText, isPrimary }` objects.
  if (primaryImage?.url) {
    product.image = {
      '@type': 'ImageObject',
      'url': primaryImage.url,
      'caption': primaryImage.altText || productName,
    }
  }
  else if (typeof primaryImage === 'string' && primaryImage) {
    // Tolerate older string-URL shape (e.g., in tests, legacy CMS records).
    product.image = {
      '@type': 'ImageObject',
      'url': primaryImage,
      'caption': productName,
    }
  }

  if (tp?.aggregate) {
    product.aggregateRating = {
      '@type': 'AggregateRating',
      'ratingValue': tp.aggregate.ratingValue,
      'reviewCount': tp.aggregate.reviewCount,
      'bestRating': 5,
      'worstRating': 1,
    }
  }
  // Per Trustpilot's guidance we emit only AggregateRating (stars/count), not
  // Review nodes — the review text stays unindexed. Reviews still render on-page.
  return product
}
